import { ENVELOPE_LAYOUT_VERSION, LEVEL_SPACINGS, POINTS_PER_TILE } from "./envelope-config";
/** Duration of one tile in seconds at the given level. */
export function getTileDuration(level) {
    return LEVEL_SPACINGS[level] * POINTS_PER_TILE[level];
}
/**
 * Returns the tile index that contains the given timestamp (seconds since Unix epoch).
 */
export function getTileIndex(timestamp, level) {
    return Math.floor(timestamp / getTileDuration(level));
}
/**
 * Returns the time range [start, end) in seconds for a given tile.
 */
export function getTileTimeRange(level, tileIndex) {
    const duration = getTileDuration(level);
    return {
        start: tileIndex * duration,
        end: (tileIndex + 1) * duration,
    };
}
/**
 * Returns all tile indices that overlap the viewport [startTime, endTime).
 * Returns an empty array if startTime >= endTime.
 */
export function getTileIndicesForViewport(startTime, endTime, level) {
    if (startTime >= endTime)
        return [];
    const firstTile = getTileIndex(startTime, level);
    // endTime is exclusive — subtract a tiny epsilon so we don't include the next tile
    // if endTime falls exactly on a tile boundary.
    const lastTile = getTileIndex(endTime - 1e-9, level);
    const indices = [];
    for (let i = firstTile; i <= lastTile; i++) {
        indices.push(i);
    }
    return indices;
}
/**
 * Returns the point index within a tile for a given timestamp.
 */
export function getPointIndexInTile(timestamp, level, tileIndex) {
    const tileRange = getTileTimeRange(level, tileIndex);
    return Math.floor((timestamp - tileRange.start) / LEVEL_SPACINGS[level]);
}
/**
 * Constructs the S3 key prefix for all tiles of a given station.
 * Format: {network}_{station}
 */
export function getStationPrefix(station) {
    return `${station.network}_${station.station}`;
}
/**
 * Inverse of getStationPrefix: "{network}_{station}" → { network, station }.
 */
export function parseStationPrefix(prefix) {
    const sep = prefix.indexOf("_");
    if (sep < 0)
        return undefined;
    const network = prefix.slice(0, sep);
    const station = prefix.slice(sep + 1);
    if (!network || !station)
        return undefined;
    return { network, station };
}
/** Encode a SEED location code as a path segment. Blank (undefined or "") becomes "--". */
export function encodeLocation(location) {
    return location ? location : "--";
}
/** Inverse of encodeLocation: "--" becomes "". */
export function decodeLocation(segment) {
    return segment === "--" ? "" : segment;
}
/**
 * Constructs the S3 key prefix for all tiles of a given station, location, and channel.
 * Format: {network}_{station}/{location}/{channel}
 */
export function getStationChannelPrefix(stationData) {
    const { channel, location } = stationData;
    return `${getStationPrefix(stationData)}/${encodeLocation(location)}/${channel}`;
}
/**
 * Constructs the S3 object key for a tile.
 * Format: {network}_{station}/{location}/{channel}/L{level}/{tileIndex}
 */
export function getTileS3Key(stationData, level, tileIndex) {
    return `${getStationChannelPrefix(stationData)}/L${level}/${tileIndex}`;
}
/**
 * Constructs the S3 root for tile keys, which includes the current layout version.
 */
export function getS3Root(prefix) {
    return `${prefix}v${ENVELOPE_LAYOUT_VERSION}/`;
}
//# sourceMappingURL=data:application/json;base64,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